// Extracted from AIWorkoutGenerator.js during Phase 4 refactor (sprint 2026-06-20).
import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import useExerciseHistory from '../../hooks/useExerciseHistory';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Zap, Loader2, Play, Dumbbell, Clock, Repeat, Timer, ChevronDown, ChevronUp,
  Battery, BatteryLow, BatteryFull, Star, ThumbsUp, Send, Info,
  AlertTriangle, Shield, Save, BookmarkCheck, ExternalLink, History, RotateCcw, Trophy, Crosshair,
  CalendarDays, Layers, Target, Wrench, HeartPulse, BookOpen, Flame, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const ENERGY_LEVELS = [
  { key: 'low', icon: BatteryLow, label_fr: 'Basse', label_en: 'Low', color: 'text-yellow-400' },
  { key: 'normal', icon: Battery, label_fr: 'Normale', label_en: 'Normal', color: 'text-green-400' },
  { key: 'high', icon: BatteryFull, label_fr: 'Haute', label_en: 'High', color: 'text-primary' },
];

const INTENSITY_LABELS = [
  { value: 1, label_fr: 'Trop facile', icon: '😴', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 2, label_fr: 'Facile', icon: '😊', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 3, label_fr: 'Parfait', icon: '💪', color: 'bg-primary/20 text-primary border-primary/30' },
  { value: 4, label_fr: 'Difficile', icon: '😤', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 5, label_fr: 'Trop dur', icon: '🥵', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

// ============ TEMPO TOOLTIP ============
export default function TempoTooltip({ language }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex">
      <button
        onClick={() => setOpen(!open)}
        className="ml-1.5 w-4 h-4 rounded-full bg-primary/20 text-primary flex items-center justify-center hover:bg-primary/30 transition-colors"
        data-testid="tempo-info-btn"
        type="button"
      >
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-6 z-50 w-64 p-3 rounded-xl border border-primary/30 bg-card shadow-xl" data-testid="tempo-tooltip">
            <p className="text-xs font-heading uppercase text-primary mb-2">
              {t('ai_workout_gen.comment_lire_le_tempo', 'Comment lire le tempo')}
            </p>
            <div className="space-y-1.5 text-[11px] text-muted-foreground">
              <p><span className="font-bold text-foreground">3-1-2</span> = 3 formats :</p>
              <p><span className="text-red-400 font-bold">3</span> sec = Phase excentrique (descente)</p>
              <p><span className="text-amber-400 font-bold">1</span> sec = Pause en bas</p>
              <p><span className="text-emerald-400 font-bold">2</span> sec = Phase concentrique (montee)</p>
              <p className="pt-1 text-[10px] italic text-primary/70">
                {t('ai_workout_gen.plus_le_tempo_est_lent_plus_le_muscle_tr', 'Plus le tempo est lent, plus le muscle travaille. Contrôle > vitesse.')}
              </p>
            </div>          </div>
        </>
      )}
    </span>
  );
}

// ============ CIBLE D'ASSAUT (Progressive Overload Target) ============
function calcAssaultTarget(lastLog) {
  if (!lastLog || !lastLog.weight || lastLog.weight <= 0) return null;

  const weight = lastLog.weight;
  const reps = lastLog.reps || 0;
  const sets = lastLog.sets || 0;
  const unit = lastLog.weight_unit || 'lbs';

  // Determine weight increment based on current weight level
  const increment = unit === 'kg'
    ? (weight >= 80 ? 1.25 : 2.5)
    : (weight >= 180 ? 2.5 : 5);

  // Strategy: If reps are high enough (>=8), suggest weight increase
  // If reps are low (<8), suggest +1 rep to build volume first
  if (reps >= 8) {
    return {
      type: 'weight',
      targetWeight: weight + increment,
      targetReps: reps,
      targetSets: sets,
      unit,
      label: `${weight + increment} ${unit}`,
      detail: `+${increment} ${unit}`,
    };
  } else {
    return {
      type: 'volume',
      targetWeight: weight,
      targetReps: reps + 1,
      targetSets: sets,
      unit,
      label: `${reps + 1} reps`,
      detail: '+1 rep',
    };
  }
}

